import { Folder } from '@/api/generatedApi';
import { useRenameFolder } from '@/hooks/api/folders/useRenameFolder';
import { createUseStyles } from '@/services/createUseStyles';
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from '@mui/material';
import { useState } from 'react';

const useStyles = createUseStyles(theme => ({
  RenameFolder_dialogContent: {
    display: 'flex',
    flexDirection: 'column',
    overflow: 'visible',

    '&:first-of-type': {
      paddingTop: theme.spacing(3),
    },
  },
}));

export const RenameFolderModal = ({
  isOpen,
  onClose,
  folder,
  onRename,
}: {
  isOpen: boolean;
  onClose: () => void;
  folder?: Folder;
  onRename?: (updatedFolder: Folder) => void;
}) => {
  const c = useStyles();

  const [newFolderName, setNewFolderName] = useState(folder?.name ?? '');
  const [error, setError] = useState<string | undefined>();

  const { renameFolder, isRenaming } = useRenameFolder({
    folder,
    onSuccess: updatedFolder => {
      onClose();
      onRename?.(updatedFolder);
    },
    onError: errorMessage => {
      setError(errorMessage);
    },
  });

  const handleEnter = () => {
    setNewFolderName(folder?.name ?? '');
    setError(undefined);
  };

  const canSubmit = newFolderName.trim() && newFolderName.trim() !== folder?.name && !isRenaming;

  const handleSubmit = () => {
    const folderName = newFolderName.trim();
    if (folderName === folder?.name) {
      onClose();
      return;
    }
    renameFolder(folderName);
  };

  const handleNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setNewFolderName(event.target.value);
    setError(undefined);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter' && canSubmit) {
      event.preventDefault();
      handleSubmit();
    }
  };

  return (
    <Dialog
      fullWidth
      open={isOpen}
      onClose={onClose}
      aria-modal='true'
      slotProps={{
        paper: {
          sx: {
            borderRadius: '12px',
            width: '405px',
          },
        },
        transition: { onEnter: handleEnter },
      }}
    >
      <DialogTitle>Rename Folder</DialogTitle>
      <DialogContent className={c.RenameFolder_dialogContent}>
        {error && (
          <Alert severity='error' sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        <TextField
          required
          label='Folder Name'
          value={newFolderName}
          onChange={handleNameChange}
          onKeyDown={handleKeyDown}
          fullWidth
          error={!!error}
          autoFocus
          onFocus={event => event.target.select()}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant='contained' onClick={handleSubmit} disabled={!canSubmit} sx={{ width: '100px' }}>
          {isRenaming ? <CircularProgress size={20} /> : 'Rename'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
